/**
 * The api's honest answer to "can I put this transfer under legal hold?" (kitecorp/transferify#85),
 * served on every `TransferDto` and mirrored here as a string union — the wire values ARE the Java enum
 * names, so no boundary mapping is needed (unlike {@link ./ExpiresAfter}, whose app-facing form is
 * numeric).
 *
 * <p>Served rather than derived here, and one of its inputs is the reason why: pinning a day-expiring
 * transfer's bytes means moving the objects' purge instruction, which on a backend that expires by key
 * PREFIX is fixed when the object is written. Whether that is possible depends on the storage provider
 * recorded on THAT row — a fact no client can see. A ui that mirrored only the shape guards would offer
 * the action on every finite-retention transfer and collect a refusal every time.
 *
 * <p>Says nothing about the PLAN. The `legalHold` entitlement is read from the catalogue on both sides,
 * exactly as for recovery and editing, so the two gates stay independent.
 */
export const TransferLegalHoldState = {
	/** Already held: the link does not expire and the transfer cannot be deleted until released. */
	HELD: "HELD",
	/** Not held, and a hold would be honoured — the only state that earns a "place hold" affordance. */
	AVAILABLE: "AVAILABLE",
	/** This transfer can never be held: no stored object, a legacy bucket, or an immovable purge date. */
	UNSUPPORTED: "UNSUPPORTED",
	/** Its own upload has not finished, so there are no bytes to preserve yet. */
	INCOMPLETE: "INCOMPLETE",
	/** Already expired: the bytes may be gone, so a hold would promise a preservation we cannot make. */
	EXPIRED: "EXPIRED",
} as const;

export type TransferLegalHoldState = typeof TransferLegalHoldState[keyof typeof TransferLegalHoldState];
